import { useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { DownloadHistoryItem } from '../../types/device';
import { BASEMAP_URLS } from '../../utils/constants';

interface DownloadDetailModalProps {
  download: DownloadHistoryItem;
  onClose: () => void;
}

function formatBytes(bytes: number | null): string {
  if (bytes == null || bytes === 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatZoomRange(levels: number[]): string {
  if (!levels.length) return '-';
  const sorted = [...levels].sort((a, b) => a - b);
  return sorted.length === 1 ? `z${sorted[0]}` : `z${sorted[0]}\u2013${sorted[sorted.length - 1]}`;
}

export function DownloadDetailModal({ download, onClose }: DownloadDetailModalProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const bounds = download.region.bounds;
    const center: [number, number] = bounds
      ? [(bounds.minLat + bounds.maxLat) / 2, (bounds.minLon + bounds.maxLon) / 2]
      : [0, 0];

    const map = L.map(mapRef.current, {
      center,
      zoom: 10,
      zoomControl: true,
      attributionControl: false,
    });

    L.tileLayer(BASEMAP_URLS.GOOGLE_SATELLITE, {
      maxZoom: 20,
    }).addTo(map);

    if (bounds) {
      const rect = L.rectangle(
        [[bounds.minLat, bounds.minLon], [bounds.maxLat, bounds.maxLon]],
        {
          color: '#06b6d4',
          weight: 2,
          fillColor: '#06b6d4',
          fillOpacity: 0.15,
          dashArray: '6 4',
        },
      );
      rect.addTo(map);
      map.fitBounds(rect.getBounds(), { padding: [40, 40] });
    }

    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [download]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700">
          <div>
            <h2 className="text-lg font-semibold text-white">{download.region.name}</h2>
            <p className="text-sm text-slate-400 mt-0.5">
              {download.device.name || download.device.type}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Map */}
        <div ref={mapRef} className="h-80 w-full" />

        {/* Details */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 px-5 py-4 border-t border-slate-700">
          <div>
            <span className="text-xs text-slate-500 uppercase tracking-wide">Tiles</span>
            <p className="text-sm text-white font-medium mt-0.5">
              {download.tiles_downloaded.toLocaleString()}
            </p>
          </div>
          <div>
            <span className="text-xs text-slate-500 uppercase tracking-wide">Zoom</span>
            <p className="text-sm text-white font-medium font-mono mt-0.5">
              {formatZoomRange(download.zoom_levels)}
            </p>
          </div>
          <div>
            <span className="text-xs text-slate-500 uppercase tracking-wide">Size</span>
            <p className="text-sm text-white font-medium mt-0.5">
              {formatBytes(download.size_bytes)}
            </p>
          </div>
          <div>
            <span className="text-xs text-slate-500 uppercase tracking-wide">Downloaded</span>
            <p className="text-sm text-white font-medium mt-0.5">
              {download.downloaded_at
                ? new Date(download.downloaded_at).toLocaleDateString('en-NZ', {
                    year: 'numeric', month: 'short', day: 'numeric',
                  })
                : '-'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
